import { MuxChevronDown2, MuxChevronUp2, MuxX } from "@/Icons";
import { ITreeItem } from "@/interfaces";
import { BareButton } from "@/shared-components";
import React, { useEffect, useState } from "react";
import styles from "./SelectableBehavior.module.scss";
import {
  SEARCHABLE_TREE_VALUE_HOLDER_TESTID,
  SEARCHABLE_TREE_CLEAR_BUTTON_TESTID,
  SEARCHABLE_TREE_TOGGLE_BUTTON_TESTID,
} from "@/constants/test-ids";
import { useShadowDomOutsideClick } from "@/hooks";

interface GenericSearchableTreeProps {
  onClear: () => void;
  children: React.ReactNode;
  value: string | undefined;
  selectedItem?: Partial<ITreeItem> | undefined;
  disabled: boolean;
  placeholder: string;
  isOpen: boolean;
  customClassName?: string;
  handleSussestionDivOpen: (isOpen: boolean) => void;
  ariaLabelledBy?: string;
}

export const SelectableBehavior = ({
  children,
  value,
  handleSussestionDivOpen,
  disabled = false,
  placeholder,
  isOpen,
  customClassName,
  onClear,
  ariaLabelledBy,
}: GenericSearchableTreeProps) => {
  const [width, setWidth] = useState<number>(0);
  const isShadowDOM = React.useRef<boolean>(false);
  const dropdownRef = useShadowDomOutsideClick<HTMLDivElement>(() => {
    handleSussestionDivOpen(false);
  });

  useEffect(() => {
    if (!dropdownRef.current) return;
    let node = dropdownRef.current?.parentNode;
    while (node) {
      if (node instanceof ShadowRoot) {
        isShadowDOM.current = true;
        break;
      }
      node = node.parentNode;
    }
  }, [dropdownRef.current]);

  useEffect(() => {
    const divElement = document.getElementsByClassName("main-body2")[0];
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries?.[0]) return;
      setWidth(dropdownRef.current?.offsetWidth || 0);
    });
    if (divElement) {
      resizeObserver.observe(divElement);
    }
    return () => {
      if (divElement) {
        resizeObserver.unobserve(divElement);
      }
    };
  }, []);

  return (
    <div
      className={`${customClassName ? customClassName : ""} ${styles.constainer} ${disabled ? "disabled" : ""}`}
      ref={dropdownRef}
    >
      <div className={`selector-input ${styles.customSelectorInput}`}>
        <div
          className="selector-current-item"
          data-testid={SEARCHABLE_TREE_VALUE_HOLDER_TESTID}
          role="button"
          aria-labelledby={ariaLabelledBy}
          tabIndex={0}
          onClick={() => !disabled && handleSussestionDivOpen(!isOpen)}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              !disabled && handleSussestionDivOpen(!isOpen);
            }
          }}
          style={{ width: "100%" }}
        >
          <span
            className={`${!value ? "selector-placeholder" : styles.selectorValue}`}
          >
            {value || placeholder}
          </span>
        </div>
        <span className="selector-controls-holder" style={{ zIndex: 100 }}>
          {value && !disabled && (
            <>
              <BareButton
                dataTestId={SEARCHABLE_TREE_CLEAR_BUTTON_TESTID}
                className="icon-holder"
                handleClick={() => {
                  !disabled && handleSussestionDivOpen(false);
                  onClear();
                }}
              >
                <MuxX />
              </BareButton>
              <div className="selector-controls-delimiter"></div>
            </>
          )}
          <BareButton
            className="icon-holder"
            dataTestId={SEARCHABLE_TREE_TOGGLE_BUTTON_TESTID}
            handleClick={(e) => {
              e.preventDefault();
              !disabled && handleSussestionDivOpen(!isOpen);
            }}
          >
            {isOpen ? <MuxChevronUp2 /> : <MuxChevronDown2 />}
          </BareButton>
        </span>
      </div>
      <div className={!isOpen || disabled ? "hidden" : ""}>
        {React.Children.map(children, (child) =>
          child
            ? // ToDo: fix typescript error
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              React.cloneElement(child, {
                width,
              })
            : child,
        )}
      </div>
    </div>
  );
};
